<template>
  <!-- Newsletter Section - Modern & Engaging -->
  <section class="newsletter-section">
    <div class="newsletter-container">
      <ClientOnly>
        <!-- Default State -->
        <div v-if="!success" class="newsletter-content">
          <!-- Left Side - Content -->
          <div class="newsletter-info">
            <div class="newsletter-badge">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/>
              </svg>
              <span>{{ $t('home.newsletter.badge') || 'Stay Informed' }}</span>
            </div>
            <h2 class="newsletter-title">{{ $t('home.newsletter.title') }}</h2>
            <p class="newsletter-subtitle">{{ $t('home.newsletter.subtitle') }}</p>

            <!-- Benefits -->
            <div class="newsletter-benefits">
              <div class="benefit-item">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                </svg>
                <span>{{ $t('home.newsletter.benefits.newArrivals') || 'New machine arrivals' }}</span>
              </div>
              <div class="benefit-item">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                </svg>
                <span>{{ $t('home.newsletter.benefits.exclusiveDeals') || 'Exclusive deals & offers' }}</span>
              </div>
              <div class="benefit-item">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                </svg>
                <span>{{ $t('home.newsletter.benefits.industryNews') || 'Industry news & insights' }}</span>
              </div>
            </div>
          </div>

          <!-- Right Side - Form Card -->
          <div class="newsletter-form-card">
            <div class="form-card-header">
              <svg class="mail-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
              </svg>
              <span>{{ $t('home.newsletter.formTitle') || 'Subscribe to Newsletter' }}</span>
            </div>

            <form @submit.prevent="handleNewsletterSignup" class="newsletter-form">
              <div class="form-field">
                <label for="newsletter-email">{{ $t('home.newsletter.form.email') || 'Email Address' }}</label>
                <input
                  id="newsletter-email"
                  v-model="form.email"
                  type="email"
                  :placeholder="$t('home.newsletter.form.emailPlaceholder') || 'your.email@example.com'"
                  class="form-input"
                  :class="{ 'has-error': errors.email }"
                  :disabled="loading"
                  required
                />
                <span v-if="errors.email" class="error-message">{{ errors.email }}</span>
              </div>

              <button type="submit" :disabled="loading || !form.email" class="submit-btn">
                <span v-if="!loading">
                  {{ $t('home.newsletter.form.subscribe') || 'Subscribe' }}
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"/>
                  </svg>
                </span>
                <svg v-else class="spinner" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" opacity="0.3"/>
                  <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                </svg>
              </button>

              <p class="privacy-note">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                </svg>
                {{ $t('home.newsletter.form.privacyText') || 'We respect your privacy. Unsubscribe anytime.' }}
              </p>
            </form>
          </div>
        </div>

        <!-- Success State -->
        <div v-else class="newsletter-success">
          <div class="success-icon">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <h3>{{ $t('home.newsletter.form.successTitle') || 'Successfully Subscribed!' }}</h3>
          <p>{{ $t('home.newsletter.form.success') }}</p>
        </div>
      </ClientOnly>
    </div>
  </section>
</template>

<script setup>
import { ref, computed } from "vue";
import { useSweetAlert } from '~/composables/useSweetAlert';

const { t } = useI18n();
const config = useRuntimeConfig();
const sweetAlert = useSweetAlert();

const loading = ref(false);
const success = ref(false);
const form = ref({
  email: ""
});
const errors = ref({});

const handleNewsletterSignup = async () => {
  errors.value = {};
  success.value = false;

  if (!form.value.email) {
    errors.value.email = t('home.newsletter.form.errors.emailRequired') || 'Email is required';
    return;
  }

  if (!form.value.email.includes("@")) {
    errors.value.email = t('home.newsletter.form.errors.emailInvalid') || 'Please enter a valid email';
    return;
  }

  try {
    loading.value = true;
    const response = await $fetch(`${config.public.apiBase}/email/subscribe`, {
      method: "POST",
      body: {
        email: form.value.email,
        preferences: {
          newsletter: true,
          product_updates: true
        },
        source: "homepage_newsletter",
        tags: ["homepage_subscriber"]
      },
    });

    if (response.success) {
      success.value = true;
      form.value.email = "";

      // Auto-hide success after 5 seconds
      setTimeout(() => {
        success.value = false;
      }, 5000);
    } else {
      throw new Error(response.message || 'Failed to subscribe');
    }
  } catch (error) {
    let errorMessage = t('home.newsletter.form.errors.generic') || 'Something went wrong';

    if (error.data?.message) {
      errorMessage = error.data.message;
    } else if (error.status === 422 && error.data?.errors?.email) {
      errorMessage = error.data.errors.email[0];
    }

    errors.value.email = errorMessage;
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.newsletter-section {
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  padding: 64px 0;
  position: relative;
  overflow: hidden;
}

.newsletter-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background:
    radial-gradient(circle at 20% 80%, rgba(251, 191, 36, 0.08) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(59, 130, 246, 0.08) 0%, transparent 50%);
  pointer-events: none;
}

.newsletter-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  position: relative;
  z-index: 1;
}

.newsletter-content {
  display: grid;
  grid-template-columns: 1fr;
  gap: 40px;
  align-items: center;
}

@media (min-width: 900px) {
  .newsletter-content {
    grid-template-columns: 1fr 400px;
    gap: 60px;
  }
}

/* Info Section */
.newsletter-info {
  color: #ffffff;
}

.newsletter-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: rgba(251, 191, 36, 0.15);
  border: 1px solid rgba(251, 191, 36, 0.3);
  border-radius: 100px;
  margin-bottom: 20px;
}

.newsletter-badge svg {
  width: 18px;
  height: 18px;
  color: #fbbf24;
}

.newsletter-badge span {
  font-size: 13px;
  font-weight: 600;
  color: #fbbf24;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.newsletter-title {
  font-size: 32px;
  font-weight: 700;
  line-height: 1.2;
  margin: 0 0 16px 0;
  color: #ffffff;
}

@media (min-width: 768px) {
  .newsletter-title {
    font-size: 40px;
  }
}

.newsletter-subtitle {
  font-size: 16px;
  line-height: 1.6;
  color: #94a3b8;
  margin: 0 0 28px 0;
  max-width: 500px;
}

/* Benefits */
.newsletter-benefits {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.benefit-item {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 15px;
  color: #cbd5e1;
}

.benefit-item svg {
  width: 20px;
  height: 20px;
  color: #22c55e;
  flex-shrink: 0;
}

/* Form Card */
.newsletter-form-card {
  background: #1e293b;
  border: 1px solid #334155;
  border-radius: 16px;
  padding: 28px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
}

.form-card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid #334155;
}

.mail-icon {
  width: 32px;
  height: 32px;
  padding: 6px;
  background: rgba(251, 191, 36, 0.15);
  border-radius: 8px;
  color: #fbbf24;
}

.form-card-header span {
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
}

/* Form */
.newsletter-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-field label {
  font-size: 14px;
  font-weight: 500;
  color: #94a3b8;
}

.form-input {
  padding: 14px 16px;
  font-size: 15px;
  background: #0f172a;
  border: 1px solid #334155;
  border-radius: 10px;
  color: #ffffff;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-input:focus {
  outline: none;
  border-color: #fbbf24;
  box-shadow: 0 0 0 3px rgba(251, 191, 36, 0.15);
}

.form-input::placeholder {
  color: #64748b;
}

.form-input.has-error {
  border-color: #ef4444;
}

.error-message {
  font-size: 13px;
  color: #f87171;
}

.submit-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 24px;
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  white-space: nowrap;
}

.submit-btn span {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(251, 191, 36, 0.3);
}

.submit-btn:active:not(:disabled) {
  transform: translateY(0);
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.submit-btn svg {
  width: 18px;
  height: 18px;
}

.spinner {
  width: 22px;
  height: 22px;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.privacy-note {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 12px;
  color: #64748b;
  margin: 0;
}

.privacy-note svg {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}

/* Success State */
.newsletter-success {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 48px 24px;
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.3);
  border-radius: 16px;
  max-width: 500px;
  margin: 0 auto;
}

.success-icon {
  width: 64px;
  height: 64px;
  background: rgba(34, 197, 94, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}

.success-icon svg {
  width: 36px;
  height: 36px;
  color: #22c55e;
}

.newsletter-success h3 {
  font-size: 22px;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 12px 0;
}

.newsletter-success p {
  font-size: 15px;
  color: #94a3b8;
  margin: 0;
  line-height: 1.5;
}

/* Mobile adjustments */
@media (max-width: 600px) {
  .newsletter-section {
    padding: 48px 0;
  }

  .newsletter-form-card {
    padding: 24px 20px;
  }

  .newsletter-title {
    font-size: 28px;
  }
}
</style>
